"use client";

import { useId } from "react";

import {
  DATA_LABEL_CLS,
  DISPLAY,
  EDITORIAL_CONTAINER_CLS,
  GROUND,
  Grain,
  INK,
  SectionMarker,
  SECTION_INTRO_CLS,
  SECTION_TITLE_CLS,
  SURFACE_DARK,
} from "@/components/editorial";
import { CSS as BRAND_CSS } from "@/components/editorial/engravings/brand";
import { F as BrandFigures } from "@/components/editorial/engravings/brand-figures";
import { Engraving } from "@/components/editorial/engravings/engraving";

import type { ConceptGroup } from "../spec";

/* ================================================================= */
/*  Copy — verbatim from the live home                                  */
/* ================================================================= */

const TWIN_STATES = [
  {
    number: "01",
    title: "Connect source evidence",
    body: "Scoped, read-only connectors bring relevant evidence from approved source systems into the Twin while those systems remain authoritative.",
  },
  {
    number: "02",
    title: "Prepare business context",
    body: "MLX proposes definitions and calculations, links them to their sources and records each approved version.",
  },
  {
    number: "03",
    title: "Review, approve and publish",
    body: "Your people review the proposals. What they approve becomes durable business context that approved AI can use.",
  },
] as const;

const ACTIVATION_STEPS = [
  {
    n: "01",
    title: "Connect priority systems",
    body: "We agree the question, connect the systems it needs and start with read-only access.",
    extra: "Days 1–5",
  },
  {
    n: "02",
    title: "Build and check your Twin",
    body: "Your Twin connects the records, defines the calculations and tests them against your data. Work that fails the checks is left out.",
    extra: "Days 6–10",
  },
  {
    n: "03",
    title: "Review the Twin, then switch it on",
    body: "Check the result against real business examples with your team. The release stays off until you switch it on.",
    extra: "End of week 2",
  },
] as const;

const SPECIMEN_QUESTION = "“What was revenue last quarter?”";

const SPECIMEN_COLUMNS = [
  {
    title: "A generic copilot",
    answer: "“Revenue was around £1.2m, based on the documents I could reach.”",
    emphasis: false,
    rows: [
      ["Source", "Whatever it could reach"],
      ["Version", "None"],
      ["Freshness", "Unknown"],
      ["Unsupported", "Answers anyway"],
    ],
  },
  {
    title: "An approved Twin answer",
    answer: "£1,243,908 · Apr–Jun 2026",
    emphasis: true,
    rows: [
      ["Approved definition", "Monthly revenue · v2"],
      ["Data current to", "30 Jun 2026"],
      ["Evidence used", "Published business view"],
      ["If unsupported", "Says so · never guesses"],
    ],
  },
] as const;

/** The four captions the hero instrument cycles through on the live home. */
const TWIN_PROCESS_STEPS = [
  { n: "01", text: "Approved sources connect", glyph: "plug" },
  { n: "02", text: "Relevant evidence gathers", glyph: "stack" },
  { n: "03", text: "Business context is proposed", glyph: "draft" },
  { n: "04", text: "Your team reviews and approves", glyph: "tick" },
] as const;

type Glyph = (typeof TWIN_PROCESS_STEPS)[number]["glyph"];

const LOOP_CAPTIONS = [
  { n: "01", text: "Connect", corner: "md:top-5 md:left-5" },
  { n: "02", text: "Prepare", corner: "md:top-5 md:right-5 md:text-right" },
  { n: "03", text: "Review", corner: "md:bottom-5 md:right-5 md:text-right" },
  { n: "04", text: "Publish", corner: "md:bottom-5 md:left-5" },
] as const;

const QUESTION_LEDGER = [
  {
    question: "“What was revenue last quarter?”",
    used: "Monthly revenue · v2",
    outcome: "Supported",
    supported: true,
  },
  {
    question: "“Which invoices are overdue over 60 days?”",
    used: "Published business view · Xero",
    outcome: "Supported",
    supported: true,
  },
  {
    question: "“Forecast next year's churn”",
    used: "No approved definition",
    outcome: "Not supported",
    supported: false,
  },
  {
    question: "“Update the supplier record”",
    used: "Write access not in a governed query",
    outcome: "Not supported",
    supported: false,
  },
] as const;

/* ================================================================= */
/*  PRC-001 · Three states                                             */
/* ================================================================= */

function ThreeStates() {
  return (
    <section
      aria-labelledby="prc-001-heading"
      className="relative overflow-hidden [--rule:color-mix(in_srgb,var(--theme-ground)_22%,transparent)] [--rule-soft:color-mix(in_srgb,var(--theme-ground)_6%,transparent)]"
      style={{ backgroundColor: SURFACE_DARK, color: GROUND }}
    >
      <Grain opacity={0.38} />
      <div className={`relative z-[2] pt-28 pb-32 max-md:pt-[5.5rem] max-md:pb-24 ${EDITORIAL_CONTAINER_CLS}`}>
        <SectionMarker section="02" label="How the Twin forms" dark />
        <div className="mt-[2.8rem] grid grid-cols-[1.25fr_0.75fr] gap-12 max-lg:grid-cols-1">
          <h2
            id="prc-001-heading"
            className={`max-w-[15ch] text-[var(--theme-ground)] ${SECTION_TITLE_CLS}`}
            style={DISPLAY}
          >
            One business. Three states you can inspect.
          </h2>
          <p className={`max-w-[35rem] text-[color-mix(in_srgb,currentColor_88%,transparent)] ${SECTION_INTRO_CLS}`}>
            You can see what the Twin read, what it derived and what your team
            approved. Source evidence, transformation and the governed result
            remain distinct and inspectable.
          </p>
        </div>
        <ol className="mt-20 grid grid-cols-3 border-y border-[var(--rule)] max-lg:grid-cols-1">
          {TWIN_STATES.map((stage) => (
            <li
              className="min-h-80 border-r border-[var(--rule-soft)] px-8 pt-[1.7rem] pb-[2.2rem] first:pl-0 last:border-r-0 max-lg:min-h-0 max-lg:border-r-0 max-lg:border-b max-lg:border-[var(--rule)] max-lg:px-0 max-lg:pt-6 max-lg:pb-8 max-lg:last:border-b-0"
              key={stage.number}
            >
              <span className="text-[length:var(--type-label)] font-semibold tracking-[0.18em] text-[var(--theme-accent)]">
                {stage.number}
              </span>
              <h3
                className="mt-20 text-2xl font-semibold tracking-[-0.02em] text-[var(--theme-ground)] max-lg:mt-[2.8rem]"
                style={DISPLAY}
              >
                {stage.title}
              </h3>
              <p className="mt-[0.9rem] leading-[1.55] text-[color-mix(in_srgb,var(--theme-ground)_88%,transparent)]">
                {stage.body}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ================================================================= */
/*  PRC-002 · Activation timeline, horizontal                          */
/* ================================================================= */

/** Minor tick strip along the rail. A userSpaceOnUse pattern keeps the
 *  ticks at a fixed pitch however wide the rail renders. */
function TickStrip({ id }: { id: string }) {
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 top-0 hidden h-3 w-full text-[var(--theme-ink)] md:block"
    >
      <defs>
        <pattern id={id} width="24" height="12" patternUnits="userSpaceOnUse">
          <line
            x1="0.5"
            y1="0"
            x2="0.5"
            y2="6"
            stroke="currentColor"
            strokeOpacity="0.32"
            strokeWidth="1"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
    </svg>
  );
}

export function ActivationTimeline({ chrome = true }: { chrome?: boolean } = {}) {
  const patternId = useId();
  return (
    <section
      aria-labelledby="prc-002-heading"
      className="relative overflow-hidden"
      style={chrome ? { backgroundColor: GROUND, color: INK } : undefined}
    >
      {chrome ? <Grain opacity={0.45} /> : null}
      <div className={chrome ? `relative z-10 py-14 sm:py-16 md:py-24 ${EDITORIAL_CONTAINER_CLS}` : "relative z-10"}>
        {chrome ? <SectionMarker section="05" label="Activation" /> : null}
        <div className={chrome ? "mt-10 max-w-3xl" : "max-w-3xl"}>
          <h2
            id="prc-002-heading"
            className={`text-[var(--theme-ink)] ${SECTION_TITLE_CLS}`}
            style={DISPLAY}
          >
            We connect the systems. You help define the result.
          </h2>
          <p className={`mt-6 text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)] ${SECTION_INTRO_CLS}`}>
            Start with one important workflow. In two focused weeks, we connect its systems and check the results with your team. We agree setup, ongoing support and changes with you.
          </p>
        </div>

        {/* The rail is divided into three equal phases, not a day scale; the
            per-phase timing labels (Days 1–5 / Days 6–10 / End of week 2)
            carry the calendar so the geometry makes no claim it cannot keep.
            Tick decoration lives on this wrapper so the <ol> holds only <li>s. */}
        <div className="relative mt-14 md:mt-20">
          <TickStrip id={patternId} />
          {/* Terminal tick — closes the rail at the end of week 2. */}
          <span
            aria-hidden="true"
            className="absolute top-0 right-0 hidden h-5 w-px bg-[var(--theme-ink)] md:block"
          />
          <ol className="relative grid grid-cols-3 border-t border-[var(--rule)] max-md:grid-cols-1 max-md:border-t-0 max-md:border-l max-md:pl-6">
            {ACTIVATION_STEPS.map((step, index) => (
              <li
                key={step.n}
                className={`relative pt-9 pb-2 md:pr-10 ${
                  index > 0 ? "max-md:border-t max-md:border-dotted max-md:border-[var(--rule)]" : ""
                } max-md:pt-6 max-md:pb-6`}
              >
                {/* Major tick at the phase start (vertical on the horizontal
                    rail; a dot against the left rail on mobile). */}
                <span
                  aria-hidden="true"
                  className="absolute top-0 left-0 hidden h-5 w-px bg-[var(--theme-ink)] md:block"
                />
                <span
                  aria-hidden="true"
                  className="absolute top-7 -left-[calc(1.5rem_+_4.5px)] size-2 bg-[var(--theme-dark)] md:hidden"
                />
                {chrome ? (
                  <p className={`text-[var(--theme-accent-text)] ${DATA_LABEL_CLS}`}>
                    {step.extra}
                  </p>
                ) : null}
                <h3
                  className="mt-3 text-[1.15rem] leading-[1.3] font-semibold tracking-[-0.01em] text-[var(--theme-ink)] md:text-[1.25rem]"
                  style={DISPLAY}
                >
                  {step.title}
                </h3>
                <p className="mt-2 max-w-[30rem] text-[0.98rem] leading-[1.6] text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]">
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

/* ================================================================= */
/*  PRC-003 · Vertical numbered steps with rail                        */
/* ================================================================= */

function VerticalSteps() {
  return (
    <div>
      <SectionMarker section="05" label="Activation" />
      <ol className="mt-8 border-l border-[var(--rule)]">
        {ACTIVATION_STEPS.map((step, index) => (
          <li
            key={step.n}
            className={`relative grid grid-cols-[3rem_1fr] items-start gap-x-4 py-6 pl-6 md:pl-8 ${
              index > 0 ? "border-t border-dotted border-[var(--rule)]" : ""
            }`}
          >
            <span
              aria-hidden="true"
              className="absolute top-[1.85rem] -left-[4.5px] size-2 bg-[var(--theme-dark)]"
            />
            <span
              className="text-[1.3rem] leading-none font-semibold tracking-[-0.01em] tabular-nums text-[var(--theme-accent-text)]"
              style={DISPLAY}
            >
              {step.n}
            </span>
            <div className="min-w-0">
              <p className={`text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}>
                {step.extra}
              </p>
              <h3
                className="mt-2 text-[1.15rem] leading-[1.3] font-semibold tracking-[-0.01em] text-[var(--theme-ink)] md:text-[1.25rem]"
                style={DISPLAY}
              >
                {step.title}
              </h3>
              <p className="mt-2 max-w-[44rem] text-[0.98rem] leading-[1.6] text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]">
                {step.body}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ================================================================= */
/*  PRC-004 · Generic copilot vs approved Twin                         */
/* ================================================================= */

function GovernedAnswerSpecimen() {
  return (
    <div className="border border-[var(--rule)]">
      <div className="flex min-h-[2.65rem] items-center justify-between gap-4 border-b border-dotted border-[var(--rule)] px-4 py-[0.7rem] max-md:flex-col max-md:items-start max-md:gap-1">
        <span className={`text-[color-mix(in_srgb,var(--theme-ground)_80%,transparent)] ${DATA_LABEL_CLS}`}>
          Exhibit · the governed result, as an agent receives it
        </span>
        <strong className={`text-[var(--theme-accent)] ${DATA_LABEL_CLS}`}>
          Illustrative specimen
        </strong>
      </div>
      <div className="border-b border-dotted border-[var(--rule)] px-4 py-4">
        <p className={`text-[color-mix(in_srgb,var(--theme-ground)_80%,transparent)] ${DATA_LABEL_CLS}`}>
          Question
        </p>
        <p className="mt-2 text-xl font-semibold tracking-[-0.01em]" style={DISPLAY}>
          {SPECIMEN_QUESTION}
        </p>
      </div>
      {/* Subgrid keeps the metadata rows of both halves on shared tracks so
          the dotted rules align even when a value wraps; browsers without
          subgrid fall back to per-column row heights. */}
      <div className="grid grid-cols-2 grid-rows-[repeat(6,auto)] max-md:grid-cols-1 max-md:grid-rows-none">
        {SPECIMEN_COLUMNS.map((column, index) => (
          <div
            className={`row-span-6 grid grid-rows-subgrid max-md:row-auto max-md:grid-rows-none ${
              index === 0
                ? "border-r border-dotted border-[var(--rule)] max-md:border-r-0 max-md:border-b"
                : ""
            }`}
            key={column.title}
          >
            <p className={`border-b border-dotted border-[var(--rule)] px-5 py-3 text-[color-mix(in_srgb,var(--theme-ground)_80%,transparent)] ${DATA_LABEL_CLS}`}>
              {column.title}
            </p>
            <p
              className={`min-h-[4.6rem] px-5 py-4 text-[1.05rem] leading-[1.5] ${
                column.emphasis
                  ? "font-semibold text-[var(--theme-ground)]"
                  : "text-[color-mix(in_srgb,var(--theme-ground)_88%,transparent)]"
              }`}
              style={DISPLAY}
            >
              {column.answer}
            </p>
            {column.rows.map(([label, value]) => (
              <div
                className="grid min-h-8 grid-cols-[minmax(9.5rem,36%)_minmax(0,1fr)] border-t border-dotted border-[var(--rule)]"
                key={label}
              >
                <span className="flex items-center border-r border-dotted border-[var(--rule)] px-3 py-2 text-[length:var(--type-label-small)] font-medium leading-tight tracking-[0.16em] text-[color-mix(in_srgb,var(--theme-ground)_80%,transparent)] uppercase">
                  {label}
                </span>
                <span
                  className={`flex items-center px-4 py-2 text-[length:var(--type-data)] font-medium tracking-[0.04em] uppercase ${
                    column.emphasis
                      ? "text-[color-mix(in_srgb,var(--theme-ground)_92%,transparent)]"
                      : "text-[color-mix(in_srgb,var(--theme-ground)_80%,transparent)]"
                  }`}
                >
                  {value}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ================================================================= */
/*  PRC-005 · Process flow rail                                        */
/* ================================================================= */

/** Hairline glyphs, drawn at one stroke weight so they read as the same
 *  instrument family as the dial ticks and dimension lines. */
const GLYPH_PATHS: Record<Glyph, string> = {
  plug: "M9 2.5v5M15 2.5v5M6 7.5h12v4a6 6 0 0 1-12 0v-4zM12 17.5v4",
  stack: "M5 4.5h14v4H5zM5 10h14v4H5zM5 15.5h14v4H5z",
  draft: "M6 3.5h8.5l4 4v13H6zM14.5 3.5v4h4M9 11.5h6M9 14.5h6M9 17.5h4",
  tick: "M5 12.5l4.5 4.5L19 7.5",
};

function ProcessGlyph({ glyph }: { glyph: Glyph }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="size-6 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="square"
      strokeLinejoin="miter"
    >
      <path d={GLYPH_PATHS[glyph]} />
    </svg>
  );
}

function ProcessFlowRail() {
  return (
    <figure className="m-0">
      <ol
        aria-label="The Twin process"
        className="grid grid-cols-4 gap-px border border-[var(--rule)] bg-[var(--rule)] max-md:grid-cols-2 max-sm:grid-cols-1"
      >
        {TWIN_PROCESS_STEPS.map((step) => (
          <li
            key={step.n}
            className="flex min-h-[9rem] flex-col justify-between gap-6 bg-[var(--theme-ground)] px-5 py-5 text-[var(--theme-ink)] max-sm:min-h-0 max-sm:flex-row max-sm:items-center max-sm:gap-4"
          >
            <ProcessGlyph glyph={step.glyph} />
            <p className="text-[length:var(--type-label-small)] font-medium leading-[1.5] tracking-[0.18em] text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] uppercase">
              <span className="text-[var(--theme-accent-text)]">{step.n}</span> · {step.text}
            </p>
          </li>
        ))}
      </ol>
      <figcaption className="mt-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-1">
        <span className={`text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}>
          The Twin process
        </span>
        <span className={`text-[var(--theme-accent-text)] ${DATA_LABEL_CLS}`}>
          Drawing 00 · The AI Twin
        </span>
      </figcaption>
    </figure>
  );
}

/* ================================================================= */
/*  PRC-006 · Loop dial with captions                                  */
/* ================================================================= */

/* The engraving bakes in its own station labels (Connect · Derive · Approve ·
   Compound). The plate annotates the dial with the site's vocabulary instead,
   so the drawn text is suppressed and only the corner captions name the
   stations. */
const LOOP_EXTRA_CSS = `
.concepts-brand.prc-loop-dial .prc-loop-figure svg{
  display:block;
  width:100%;
  height:auto;
}
.concepts-brand.prc-loop-dial .prc-loop-figure svg text{
  display:none;
}
`;

function LoopDial() {
  return (
    <div className="concepts-brand prc-loop-dial relative border border-[var(--rule)]">
      <style>{BRAND_CSS + LOOP_EXTRA_CSS}</style>
      <div className="flex min-h-[2.65rem] items-center justify-between gap-4 border-b border-dotted border-[var(--rule)] px-4 py-[0.7rem] max-md:flex-col max-md:items-start max-md:gap-1">
        <span className={`text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}>
          Plan, the operating loop
        </span>
        <strong className={`text-[var(--theme-accent-text)] ${DATA_LABEL_CLS}`}>
          Reviewed by your team
        </strong>
      </div>
      <div className="relative px-5 py-8 md:px-20 md:py-12">
        <div className="prc-loop-figure mx-auto w-full max-w-[30rem]">
          <Engraving data={BrandFigures.loopDial} />
        </div>
        {/* Kept as a real box (not display:contents) so the list and its
            label survive in the accessibility tree; at md+ it overlays the
            plate and the items pin to its corners. */}
        <ul
          aria-label="Stages of the operating loop"
          className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 md:pointer-events-none md:absolute md:inset-0 md:mt-0 md:block"
        >
          {LOOP_CAPTIONS.map((caption) => (
            <li
              key={caption.n}
              className={`flex items-center gap-3 text-[var(--theme-ink)] md:absolute ${caption.corner}`}
            >
              <span
                className="text-[1.3rem] leading-none font-semibold tracking-[-0.01em] tabular-nums text-[var(--theme-accent-text)]"
                style={DISPLAY}
              >
                {caption.n}
              </span>
              <span className={`text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}>
                {caption.text}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ================================================================= */
/*  PRC-007 · Prepared results note                                    */
/* ================================================================= */

function PreparedResultsNote() {
  return (
    <section
      aria-labelledby="prc-007-heading"
      className="relative overflow-hidden"
      style={{ backgroundColor: SURFACE_DARK, color: GROUND }}
    >
      <Grain opacity={0.38} />
      <div className={`relative z-[2] py-16 md:py-24 ${EDITORIAL_CONTAINER_CLS}`}>
        <div className="grid grid-cols-[0.8fr_1.2fr] gap-12 border-y border-[var(--rule)] py-10 max-lg:grid-cols-1 max-lg:gap-6">
          <div>
            <p className={`text-[var(--theme-accent)] ${DATA_LABEL_CLS}`}>
              Prepared business logic
            </p>
            {/* H2 here: the band stands alone in this group, so it must not
                skip a level. On the live home it is the H3 under section 02. */}
            <h2
              id="prc-007-heading"
              className="mt-3 max-w-[16ch] text-[clamp(1.8rem,3vw,2.7rem)] leading-[1.05] font-semibold tracking-[-0.03em] text-[var(--theme-ground)]"
              style={DISPLAY}
            >
              Fast answers from approved calculations.
            </h2>
          </div>
          <div>
            <p className="max-w-[42rem] text-[1.05rem] leading-[1.65] text-[color-mix(in_srgb,var(--theme-ground)_90%,transparent)]">
              MLX prepares approved business calculations before AI needs them.
              That produces significantly faster answers, consistent numbers and
              lower model costs.
            </p>
            <p className="mt-4 max-w-[42rem] text-[0.98rem] leading-[1.65] text-[color-mix(in_srgb,var(--theme-ground)_80%,transparent)]">
              When the approved calculation and source data have not changed, the
              result does not change either. Its evidence stays attached.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ================================================================= */
/*  PRC-008 · Question ledger                                          */
/* ================================================================= */

const LEDGER_HEAD_CLS = `px-4 py-3 text-left text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`;
const LEDGER_DATA_CLS =
  "px-4 py-3 align-top text-[length:var(--type-data)] font-medium tracking-[0.04em] uppercase";
const LEDGER_QUESTION_CLS =
  "text-left text-[1rem] leading-[1.45] font-semibold tracking-[-0.01em] text-[var(--theme-ink)]";

function usedTone(supported: boolean) {
  return supported
    ? "text-[var(--theme-ink)]"
    : "text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)]";
}

function outcomeTone(supported: boolean) {
  return supported
    ? "text-[var(--theme-accent-text)]"
    : "text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)]";
}

function OutcomeMark({ supported, label }: { supported: boolean; label: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span
        aria-hidden="true"
        className={`size-2 shrink-0 ${
          supported
            ? "bg-[var(--theme-dark)]"
            : "border border-[color-mix(in_srgb,var(--theme-ink)_60%,transparent)]"
        }`}
      />
      {label}
    </span>
  );
}

/** Small-screen rendering of the ledger: one stacked block per question so
 *  the Outcome column (the point of the exhibit) is never off-screen. */
function QuestionLedgerStacked() {
  return (
    <ol className="md:hidden">
      {QUESTION_LEDGER.map((row, index) => (
        <li
          key={row.question}
          className={index > 0 ? "border-t border-dotted border-[var(--rule)]" : ""}
        >
          <p className={`px-4 pt-3 pb-2 ${LEDGER_QUESTION_CLS}`} style={DISPLAY}>
            {row.question}
          </p>
          <dl>
            <div className="grid min-h-8 grid-cols-[minmax(9.5rem,36%)_minmax(0,1fr)] border-t border-dotted border-[var(--rule)]">
              <dt className="flex items-center border-r border-dotted border-[var(--rule)] px-3 py-2 text-[length:var(--type-label-small)] font-medium leading-tight tracking-[0.16em] text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] uppercase">
                Used
              </dt>
              <dd className={`flex items-center px-4 py-2 text-[length:var(--type-data)] font-medium tracking-[0.04em] uppercase ${usedTone(row.supported)}`}>
                {row.used}
              </dd>
            </div>
            <div className="grid min-h-8 grid-cols-[minmax(9.5rem,36%)_minmax(0,1fr)] border-t border-dotted border-[var(--rule)]">
              <dt className="flex items-center border-r border-dotted border-[var(--rule)] px-3 py-2 text-[length:var(--type-label-small)] font-medium leading-tight tracking-[0.16em] text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] uppercase">
                Outcome
              </dt>
              <dd className={`flex items-center px-4 py-2 text-[length:var(--type-data)] font-medium tracking-[0.04em] uppercase ${outcomeTone(row.supported)}`}>
                <OutcomeMark supported={row.supported} label={row.outcome} />
              </dd>
            </div>
          </dl>
        </li>
      ))}
    </ol>
  );
}

function QuestionLedger() {
  return (
    <div className="border border-[var(--rule)]">
      <div className="flex min-h-[2.65rem] items-center justify-between gap-4 border-b border-dotted border-[var(--rule)] px-4 py-[0.7rem] max-md:flex-col max-md:items-start max-md:gap-1">
        <span className={`text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}>
          Exhibit · what a governed query will and will not answer
        </span>
        <strong className={`text-[var(--theme-accent-text)] ${DATA_LABEL_CLS}`}>
          Illustrative specimen
        </strong>
      </div>
      <QuestionLedgerStacked />
      {/* The table keeps its three columns from md up; below that the stacked
          ledger above takes over, so nothing scrolls sideways unsignposted. */}
      <div className="overflow-x-auto max-md:hidden">
        <table className="w-full min-w-[40rem] border-collapse">
          <caption className="sr-only">
            Questions put to the Twin, the approved context each one used and
            whether the governed query supported it.
          </caption>
          <thead>
            <tr className="border-b border-dotted border-[var(--rule)]">
              <th scope="col" className={`w-[40%] ${LEDGER_HEAD_CLS}`}>
                Question
              </th>
              <th scope="col" className={`w-[36%] border-l border-dotted border-[var(--rule)] ${LEDGER_HEAD_CLS}`}>
                What the Twin used
              </th>
              <th scope="col" className={`border-l border-dotted border-[var(--rule)] ${LEDGER_HEAD_CLS}`}>
                Outcome
              </th>
            </tr>
          </thead>
          <tbody>
            {QUESTION_LEDGER.map((row) => (
              <tr key={row.question} className="border-b border-dotted border-[var(--rule)] last:border-b-0">
                <th
                  scope="row"
                  className={`px-4 py-3 align-top ${LEDGER_QUESTION_CLS}`}
                  style={DISPLAY}
                >
                  {row.question}
                </th>
                <td className={`border-l border-dotted border-[var(--rule)] ${LEDGER_DATA_CLS} ${usedTone(row.supported)}`}>
                  {row.used}
                </td>
                <td className={`border-l border-dotted border-[var(--rule)] ${LEDGER_DATA_CLS} ${outcomeTone(row.supported)}`}>
                  <OutcomeMark supported={row.supported} label={row.outcome} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex min-h-[2.2rem] flex-wrap items-center gap-x-4 gap-y-1 border-t border-dotted border-[var(--rule)] px-4 py-[0.55rem]">
        <span className={`text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}>
          If unsupported
        </span>
        <span className="text-[length:var(--type-data)] font-medium tracking-[0.04em] text-[var(--theme-accent-text)] uppercase">
          Says so · never guesses
        </span>
      </div>
    </div>
  );
}

/* ================================================================= */
/*  PRC-009 · Authoring chain                                          */
/* ================================================================= */

/** The build phase as a chain of custody: who does each step, in plain
 *  words. Actors only — the fingerprinting scheme, ranking maths and
 *  audit tooling stay off the site. Every claim here is carried by the
 *  authoring pipeline (deterministic discovery → authoring agent →
 *  compiler → database proofs → independent evaluation → fail-closed
 *  admission → sealed release, dark until activated). */
const AUTHORING_CHAIN = [
  {
    actor: "Find the connections",
    line: "Find records that describe the same customer or supplier across systems. Sensitive values are protected before comparison.",
  },
  {
    actor: "AI proposes a design",
    line: "AI proposes relationships and definitions, such as what counts as overdue, using descriptions and measurements rather than raw records.",
  },
  {
    actor: "Software writes the code",
    line: "Software turns the proposed definitions into calculations and tests.",
  },
  {
    actor: "Check against your data",
    line: "Results must reconcile against your source data, such as invoice balances and totals.",
  },
  {
    actor: "A separate AI reviews it",
    line: "A different AI evaluates the work. It cannot rewrite or approve the design it reviews.",
  },
  {
    actor: "Release only checked work",
    line: "Work that fails the checks is blocked. The released version stays off until you switch it on.",
  },
] as const;

export function AuthoringChain() {
  return (
    <div className="border border-[var(--rule)]">
      <div className="flex min-h-[2.65rem] items-center justify-between gap-4 border-b border-dotted border-[var(--rule)] px-4 py-[0.7rem] max-md:flex-col max-md:items-start max-md:gap-1">
        <span className={`text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}>
          How your Twin is built and checked
        </span>
        <strong className={`text-[var(--theme-accent-text)] ${DATA_LABEL_CLS}`}>
          Six steps · separate review
        </strong>
      </div>
      <ol>
        {AUTHORING_CHAIN.map((step, index) => (
          <li
            key={step.actor}
            className={`grid grid-cols-[minmax(12rem,34%)_minmax(0,1fr)] max-sm:grid-cols-1 ${
              index > 0 ? "border-t border-dotted border-[var(--rule)]" : ""
            }`}
          >
            <div className="flex items-baseline gap-3 px-4 pt-3 pb-1 sm:border-r sm:border-dotted sm:border-[var(--rule)] sm:py-3">
              <span className={`text-[var(--theme-accent-text)] ${DATA_LABEL_CLS}`}>
                {String(index + 1).padStart(2, "0")}
              </span>
              <span
                className="text-[1rem] leading-[1.35] font-semibold tracking-[-0.01em] text-[var(--theme-ink)]"
                style={DISPLAY}
              >
                {step.actor}
              </span>
            </div>
            <p className="px-4 pt-1 pb-3 text-[0.95rem] leading-[1.55] text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] sm:py-3">
              {step.line}
            </p>
          </li>
        ))}
      </ol>
      <div className="flex min-h-[2.2rem] flex-wrap items-center gap-x-4 gap-y-1 border-t border-dotted border-[var(--rule)] px-4 py-[0.55rem]">
        <span className={`text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}>
          The reviewer
        </span>
        <span className="text-[length:var(--type-data)] font-medium tracking-[0.04em] text-[var(--theme-accent-text)] uppercase">
          Never the author
        </span>
      </div>
    </div>
  );
}

/* ================================================================= */
/*  Group                                                              */
/* ================================================================= */

export const PROCESS_GROUP: ConceptGroup = {
  id: "process",
  prefix: "PRC",
  title: "How it works",
  blurb:
    "How the Twin forms and how a focused two-week activation runs: the three inspectable states, the timeline, the governed answer beside a generic copilot, and the loop your team reviews.",
  specs: [
    {
      id: "PRC-001",
      title: "Three states",
      note: "The live section 02 rebuilt: marker, H2, intro and the three-column ordered list of Twin states on the dark surface.",
      surface: "dark",
      width: "full",
      render: () => <ThreeStates />,
    },
    {
      id: "PRC-002",
      title: "Activation timeline, horizontal",
      note: "Section 05's three phases laid along a hairline rail with tick marks; collapses to a left rail on small screens.",
      surface: "ground",
      width: "full",
      render: () => <ActivationTimeline />,
    },
    {
      id: "PRC-003",
      title: "Vertical numbered steps with rail",
      note: "The same activation steps as a vertical ordered list against a left rail, numerals in accent text and timing as a data label.",
      surface: "ground",
      width: "contained",
      render: () => <VerticalSteps />,
    },
    {
      id: "PRC-004",
      title: "Generic copilot vs approved Twin",
      note: "The GovernedAnswerSpecimen exhibit from the home: question row, two subgrid columns, dotted ledger rows, on the dark surface.",
      surface: "dark",
      width: "contained",
      render: () => <GovernedAnswerSpecimen />,
    },
    {
      id: "PRC-005",
      title: "Process flow rail",
      note: "The four hero-instrument captions as a gap-px strip with hairline glyphs (plug, stack, draft, tick) and a figure caption.",
      surface: "ground",
      width: "contained",
      render: () => <ProcessFlowRail />,
    },
    {
      id: "PRC-006",
      title: "Loop dial with captions",
      note: "The BrandFigures.loopDial engraving on the cream dot grid, framed as a plate with four corner captions: Connect, Prepare, Review, Publish.",
      surface: "ground",
      width: "contained",
      render: () => <LoopDial />,
    },
    {
      id: "PRC-007",
      title: "Prepared results note",
      note: "The PreparedResultsNote split band from section 02 (label and H3 left, two paragraphs right) as its own dark band.",
      surface: "dark",
      width: "full",
      render: () => <PreparedResultsNote />,
    },
    {
      id: "PRC-008",
      title: "Question ledger",
      note: "Exhibit table of four illustrative questions: what the Twin used and whether the governed query supported it, with the unsupported rule in the footer.",
      surface: "ground",
      width: "contained",
      render: () => <QuestionLedger />,
    },
    {
      id: "PRC-009",
      title: "Authoring chain",
      note: "The build phase as a chain of custody: six workers from measurement to admission, actor and deed per row, with the reviewer-is-never-the-author rule in the footer. Serves the landing's Behind-the-receipt band.",
      surface: "ground",
      width: "contained",
      render: () => <AuthoringChain />,
    },
  ],
};
